import moment from "moment";
import React, { useContext, useEffect, useState } from "react";
import {
	Button,
	Form,
	Modal, OverlayTrigger, Tooltip
} from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import { renderToStaticMarkup } from 'react-dom/server';
import { useTranslation, withTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { LoaderContext, PollerContext, PreferenceContext, RegulationContext } from "../../../App";
import ReportingPeriod from "../../../components/Regulation/ReportingPeriod";
import { AIReportJobStatus, CategoryType, LS_AI_CONFIG_KEY, LS_AUTHORITY_KEY, LS_COMPANY_INFO_KEY, LS_UI_ELEMENTS_CONFIG_KEY, PollerAction, PollerType, ReportBasis, ReportPageType, ReportSettingsFrequency, ReportStatus, languageOptions, startYear } from "../../../core/constants/common";
import CompanyService from "../../../core/service/CompanyService";
import ESGDataSetService from "../../../core/service/ESGDataSetService";
import GeneralDisclosureService from "../../../core/service/GeneralDisclosureService";
import MatAssessmentService from "../../../core/service/MatAssessmentService";
import ReportAIService from "../../../core/service/ReportAIService";
import ReportDataService from "../../../core/service/ReportDataService";
import ReportService from "../../../core/service/ReportService";
import StandardService from "../../../core/service/StandardService";
import ESRSReportingUtils from "../../../core/utilities/ESRSReportingUtils";
import GHGReportingUtils from "../../../core/utilities/GHGReportingUtils";
import UAEFDLReportingUtils from "../../../core/utilities/UAEFDLReportingUtils";
import ReportingUtils from "../../../core/utilities/ReportingUtils";
import { ensureLanguageLoaded } from "../../../core/utilities/TranslationHelpers";
import Utils from "../../../core/utilities/Utils";
import VSMEReportingUtils from "../../../core/utilities/VSMEReportingUtils";
import esg_ai_pic from "../../../images/esg_ai_Green.png";
import scratch_icon from "../../../images/scratch_icon.png";
import DataViewNew from "../../Common/Dataview/PeriodicDataView/DataViewNew";
import AIConfiguration from "../CompanySettings/AIConfiguration";
import GeneralDisclosure from "../CompanySettings/GeneralDisclosure";
import MaterialTopic from "../CompanySettings/MaterialTopic";
import ReportGenProgressCard from "./ReportGenProgressCard";
import './Reporting.scss';
import ReportingStandardData from "./ReportingStandardData";

const CardInDisplay = {
	REPORT_FORM: 1,
	DATA_COMPLETION: 2,
	TEMPLATE_CHOOSER: 3,
	REPORT_GEN_LOADER: 4,
	REGENERATE_REPORT: 5
};

const isGhgLikeReportStandard = (standard) => standard === 'GHG' || standard === 'UAE_FDL';

function NewReportModal({ t, onHide, activeView, reportGenerationCallback, editedReport, activeTab, view }) {
	const lsData = JSON.parse(localStorage.getItem(LS_AUTHORITY_KEY));
	const uiElementsConfig = JSON.parse(localStorage.getItem(LS_UI_ELEMENTS_CONFIG_KEY)) ?? null;
	const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY));
	const companyId = lsData.profile.company.id;
	const currentYear = new Date().getFullYear();
	const currentRoute = window.location.pathname;
	const aiConfig = JSON.parse(localStorage.getItem(LS_AI_CONFIG_KEY) ?? null);
	const aiPersonality = aiConfig?.personality?.personality_name ?? '';
	const location = useLocation()
	const navigate = useNavigate();
	const [showParentModal, setShowParentModal] = useState(true);
	const [show, setShow] = useState(true);
	const [standards, setStandards] = useState([]);
	const [filter, setFilter] = useState({
		standardId: 0,
		year: editedReport?.period ? Utils.getYearFrom(editedReport.period) : currentYear,
		reportStandard: activeView,
		name: editedReport?.name ?? `${activeView} Report V1.0 - ${moment().format('DD.MM.YYYY')}`,
		language: editedReport?.lang ?? 'en-us',
		periodStart: '',
		periodEnd: '',
		basis: editedReport?.basis ?? ReportBasis.ORG,
		entities: editedReport?.entities ?? []
	});

	const [validated, setValidated] = useState(false);
	const { setLoaderDisplay } = useContext(LoaderContext);
	const { pollerData, setPollerData } = useContext(PollerContext);
	const { glbRegulation } = useContext(RegulationContext);
	const [showConfirmation, setShowConfirmation] = useState(false);
	const glbRegulationId = glbRegulation?.id ? glbRegulation.id : "";
	const [stdTypes, setStdTypes] = useState([]);
	const [dataCompletionProgress, setDataCompletionProgress] = useState([]);
	const [activeCard, setActiveCard] = useState(editedReport?.status === 'DRAFT' ? CardInDisplay.REGENERATE_REPORT : CardInDisplay.REPORT_FORM);
	const [activeReport, setActiveReport] = useState(editedReport ?? '');
	const [gdshow, setGdShow] = useState(false);
	const [qualshow, setQualShow] = useState(false);
	const [quantshow, setQuantshow] = useState(false);
	const [navAIShow, setnavAIShow] = useState(false);
	const { k, i18n } = useTranslation();
	const { preference } = useContext(PreferenceContext);
	const [esgTopics, setESGTopics] = useState([]);
	const [reportDataUpdateInProgress, setReportDataUpdateInProgress] = useState(false);
	const [branches, setBranches] = useState([]);
	const [isLoadingBranches, setIsLoadingBranches] = useState(false);
	const [reportSettingsFreq, setReportSettingsFreq] = useState(ReportSettingsFrequency.CALENDAR_YEAR);

	useEffect(() => {
		if (!glbRegulationId) return;
		fetchStandards();
		fetchStandardTypes();
		fetchLanguageSettings();
		loadBranches();
	}, [glbRegulationId]);

	useEffect(() => {
		if (!glbRegulationId) return;
		fetchESGTopics();
	}, [glbRegulationId, filter.language]);

	// Set basis to ORG when GHG or UAE-FDL standard is selected
	useEffect(() => {
		if (isGhgLikeReportStandard(filter.reportStandard)) {
			setFilter(prevFilter => ({ ...prevFilter, basis: ReportBasis.ORG, entities: [] }));
		}
	}, [filter.reportStandard]);

	useEffect(() => {
		if (pollerData.type === PollerType.REPORT_BUILDER
			&& pollerData.jobId === 0
			&& pollerData.jobType === 'CO_DRAFT'
			&& pollerData.action === PollerAction.STOP
			&& !reportDataUpdateInProgress) {
			if (pollerData.status === AIReportJobStatus.COMPLETED && pollerData.reportData) {
				navigate('/company_admin/reporting?id=' + pollerData.reportData?.id
					+ '&mode=EDIT&regulationId=' + pollerData.reportData?.standardId
					+ '&year=' + pollerData.reportData?.year
					+ '&stdType=' + pollerData.reportData?.reportStandard
					+ '&coDraft=' + true
					+ '&jobId=' + pollerData.reportData?.jobId
					+ '&activeTab=' + activeTab,
					{ state: { esgData: pollerData.reportData.esgData } });
				// Reset poller data
				setPollerData({
					action: PollerAction.IDLE,
					companyId: 0,
					type: PollerType.NONE,
					jobId: 0,
					status: '',
					progress: '',
					jobType: 'AUTO_DRAFT'
				});
			} else if (pollerData.status === AIReportJobStatus.FAILED) {
				exitModal();
				// Reset poller data
				setPollerData({
					action: PollerAction.IDLE,
					companyId: 0,
					type: PollerType.NONE,
					jobId: 0,
					status: '',
					progress: '',
					jobType: 'AUTO_DRAFT'
				});
			}
		}
	}, [pollerData, reportDataUpdateInProgress]);

	const exitModal = () => {
		setShow(false);
		onHide();
	};

	// Fetch only parent branches
	const loadBranches = async () => {
		setIsLoadingBranches(true);
		try {
			const res = await CompanyService.getEnterprisesByLevel(1);
			if (res.status === 200) {
				const sortedBranches = res.data.sort((a, b) => a.sortOrder - b.sortOrder);
				setBranches(sortedBranches);
			}
		} catch (error) {
			console.error('Error loading branches:', error);
		} finally {
			setIsLoadingBranches(false);
		}
	};

	const fetchLanguageSettings = () => {
		ReportService.getReportSettings()
			.then(res => {
				if (res.status !== 200) return;

				const frequency = res.data.frequency || ReportSettingsFrequency.CALENDAR_YEAR;
				let { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(frequency, currentYear);
				// Handle edited report period parsing
				if (editedReport?.period) {
					const parsedYear = Utils.getYearFrom(editedReport.period);
					if (editedReport.periodEnd) {
						// Use existing periodEnd if available
						defaultPeriodEnd = editedReport.periodEnd;
					} else {
						// Set periodEnd to Dec 31 of parsed year if no periodEnd exists
						defaultPeriodEnd = `${parsedYear}-12-31`;
					}
					// Set periodStart to Jan 1 of parsed year
					defaultPeriodStart = `${parsedYear}-01-01`;
				}
				setReportSettingsFreq(frequency);
				setFilter(prevFilter => ({
					...prevFilter,
					language: res.data.language,
					periodStart: defaultPeriodStart,
					periodEnd: defaultPeriodEnd
				}));
			}).catch(err => { });
	}

	const fetchStandards = () => {
		setFilter({ ...filter, standardId: glbRegulationId });
		CompanyService.getCompanySubscriptions(companyId, 'S')
			.then(res => {
				if (res.status !== 200) return;

				setStandards(res.data?.regulations ?? []);

			}).catch(err => { });
	};

	const fetchStandardTypes = () => {
		ESGDataSetService.getPlatformStandard().then(res => {
			if (res.status !== 200) return;

			const standardTypes = res.data.data || [];
			// Add BRSR-OLD if BRSR exists in the list
			if (standardTypes.includes('BRSR') && !standardTypes.includes('BRSR-OLD')) {
				standardTypes.push('BRSR-OLD');
			}
			setStdTypes(res.data.data);
		}).catch(err => { });
	};

	const onNewReport = () => {
		if (!filter.standardId || !filter.reportStandard) {
			setValidated(true);
			return;
		}
		const standard = standards?.find(s => s.id === filter.standardId);
		if (!standard) return;

		if (filter.reportStandard === 'GRI') {
			navigate('/company_admin/reporting?regulation=' + standard.name
				+ '&regulationId=' + standard.id
				+ '&frequency=' + standard.frequency
				+ '&year=' + filter.periodStart.split('-')[0]
				+ '&periodStart=' + filter.periodStart
				+ '&periodEnd=' + filter.periodEnd
				+ '&stdType=' + filter.reportStandard
				+ '&entities=' + filter.entities.join(',')
			);
		} else if (filter.reportStandard === 'BRSR-OLD') {
			navigate(
				'/company_admin/BRSR'
				+ '?regulationId=' + standard.id
				+ '&year=' + filter.year
				+ '&regulation=' + standard.name
				+ '&stdType=BRSR'
			);
		} else if (isGhgLikeReportStandard(filter.reportStandard) || filter.reportStandard === 'BRSR') {
			navigate(
				'/company_admin/reporting'
				+ '?regulationId=' + standard.id
				+ '&year=' + filter.year
				+ '&regulation=' + standard.name
				+ '&stdType=' + filter.reportStandard
				+ '&view=' + view
			);
		}
		exitModal();
	};


	const onGenerateAIReport = () => {
		if (!filter.standardId || !filter.reportStandard) {
			setValidated(true);
			return;
		}
		setShowConfirmation(true);
		setShowParentModal(false)
	};

	const fetchMetricAndDiclosureData = () => {
		const standard = standards?.find(s => s.id === filter.standardId);

		if (!standard) return;
		const lang = (filter.language == 'en-us' || filter.language == 'en-uk') ? 'en' : filter.language;
		const metricReq = StandardService.getMetricData({
			stdId: standard.id,
			period: filter.periodStart,
			standardTypes: [filter.reportStandard, 'ESRS'],
			language: lang,
			periodStart: filter.periodStart,
			periodEnd: filter.periodEnd,
		});
		const discReq = GeneralDisclosureService.getQuestionsAndAnswers({
			stdType: filter.reportStandard,
			period: filter.periodStart,
			lang: lang,
			periodStart: filter.periodStart, periodEnd: filter.periodEnd,
		});
		setLoaderDisplay(true);
		Promise.all([metricReq, discReq]).then(async (res) => {
			const metricData = res[0].data;
			const discData = res[1].data.data;
			await setupReportAndSave({
				standardId: standard.id,
				metricData: metricData,
				discData: discData,
			});
		}).catch(err => {
			setLoaderDisplay(false);
			setShowConfirmation(false);
		});
	};

	const setupReportAndSave = async ({ standardId, metricData, discData }) => {
		const report = {
			id: activeReport.id,
			name: activeReport.name,
			status: ReportStatus.DRAFT,
			sections: [],
			regulationId: standardId,
			companyId: companyId,
		};
		const language = filter.language || i18n.language;
		// Ensure language resources are loaded before generating report sections
		await ensureLanguageLoaded(i18n, language);
		
		report.sections.push(ReportingUtils.getCoverSection(report, companyId, t, language, i18n));
		report.sections.push(ReportingUtils.getTableOfContentsSection(t, language, i18n));
		report.sections.push(ReportingUtils.getLeadershipSection(t, language, i18n));
		report.sections.push(ReportingUtils.getAboutReportSection(t, language, i18n));
		report.sections.push(ReportingUtils.getAboutCompanySection(t, language, i18n));
		report.sections.push(ReportingUtils.getOurESGApproachSection(t, language, i18n));
		report.sections.push(ReportingUtils.getEnvironmentSection(esgTopics?.filter(t => t.categoryType === CategoryType.ENVIRONMENTAL) ?? [], language, i18n));
		report.sections.push(ReportingUtils.getSocialSection(esgTopics?.filter(t => t.categoryType === CategoryType.SOCIAL) ?? [], language, i18n));
		report.sections.push(ReportingUtils.getGovernanceSection(esgTopics?.filter(t => t.categoryType === CategoryType.GOVERNANCE) ?? [], language, i18n));
		report.sections.push(ReportingUtils.getESGDisclosureSection(language, i18n));
		report.sections.push(ReportingUtils.getBackCover(null, language, i18n));
		report.sections?.forEach((section, sIndex) => {
			section.index = sIndex;
			section.sortOrder = sIndex + 1;
			section?.pages?.forEach((page, pIndex) => {
				page.index = pIndex;
				page.sortOrder = pIndex + 1;
			});
		});
		ReportingUtils.applyGriTableOfContentsToReport(report, t('TableofContents.label'));
		let griSectionIndex = -1, griPageIndex = -1, sustSectionIndex = -1, sustPageIndex = -1;
		for (let i = 0; i < report.sections?.length; i++) {
			const section = report.sections[i];
			for (let j = 0; j < section?.pages?.length; j++) {
				const page = section.pages[j];
				if (page.type === ReportPageType.METRIC_DATA) {
					griSectionIndex = i;
					griPageIndex = j;
				} else if (page.type === ReportPageType.SUST_CONTENT_INDEX) {
					sustSectionIndex = i;
					sustPageIndex = j;
				}
			}
		}
		if (griSectionIndex >= 0 && griPageIndex >= 0) {
			// Set metric data as JSON
			report.sections[griSectionIndex].pages[griPageIndex].json = metricData;
			const questions = discData?.questions ?? [];
			// Update disclosure questions in the page
			const pageHTML = report.sections[griSectionIndex].pages[griPageIndex].html;
			const renderedMetricHTML = renderToStaticMarkup(
				<ReportingStandardData
					page={report.sections[griSectionIndex].pages[griPageIndex]}
					disclosureQuestions={questions}
					periodStart={filter.periodStart}
					periodEnd={filter.periodEnd} />
			);
			const formattedHTML = `${pageHTML}${renderedMetricHTML}`;
			report.sections[griSectionIndex].pages[griPageIndex].html = formattedHTML;
		}
		if (sustSectionIndex >= 0 && sustPageIndex >= 0) {
			report.sections[sustSectionIndex].pages[sustPageIndex].json = metricData;
			const pageHTML = report.sections[sustSectionIndex].pages[sustPageIndex].html;
			const renderedMetricHTML = renderToStaticMarkup(
				<ReportingStandardData
					page={report.sections[sustSectionIndex].pages[sustPageIndex]}
					disclosureQuestions={[]}
					periodStart={filter.periodStart}
					periodEnd={filter.periodEnd} />
			);
			const formattedHTML = `${pageHTML}${renderedMetricHTML}`;
			report.sections[sustSectionIndex].pages[sustPageIndex].html = formattedHTML;
		}
		report.sections?.forEach(section => {
			section?.pages?.forEach((page, index) => {
				let trimmedHTML = page.html?.replace(/>\s+</g, '><');
				page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
				page.jsonContent = page.json ? JSON.stringify(page.json) : null;
				delete page.html;
				delete page.json;
				delete page.index;
			});
			delete section.index;
		});
		report.regulationId = standardId;
		report.companyId = companyId;
		report.generateContent = true;
		report.period = filter.periodStart;
		report.periodEnd = filter.periodEnd;
		report.entities = filter.entities;
		report.companyInfo = companyInfo;
		// const lang = (filter.language == 'en-us' || filter.language == 'en-uk') ? 'en' : filter.language;
		report.lang = filter.language;
		ReportService.updateReport(activeReport.id, report).then(res => {
			if (res.status === 200) {
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				setPollerData({
					action: PollerAction.START,
					companyId: companyId,
					type: PollerType.REPORT_BUILDER,
					jobId: res.data.jobId,
					status: AIReportJobStatus.IN_PROGRESS,
					progress: '',
					jobType: 'AUTO_DRAFT'
				});
			}
			setLoaderDisplay(false);
			setShowConfirmation(false);
			exitModal();
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				if (currentRoute?.indexOf('/reporting_list') >= 0) {
					reportGenerationCallback();
				}
			}
		}).catch(error => {
			toast.error(error.title, {
				position: toast.POSITION.TOP_RIGHT,
			});
			setLoaderDisplay(false);
			setShowConfirmation(false);
		});
	};


	const renderFilter = () => {
		const options = [];
		for (let i = currentYear + 1; i >= startYear; i--) {
			options.push(<option value={i} key={i}>{i}</option>);
		}
		return (

			<Form noValidate validated={validated}>
				<div className="row text-start m-0">
					{
						(filter.reportStandard === 'GRI' || filter.reportStandard === 'ESRS' || filter.reportStandard === 'VSME' || isGhgLikeReportStandard(filter.reportStandard) || filter.reportStandard === 'BRSR') &&

						<div className="col-lg-12 p-2">
							<label className="mb-1">{t("NameOfTheReport.label")} *</label>
							<input
								type="text"
								className="form-control"
								onChange={(e) => setFilter({ ...filter, name: e.target.value })}
								value={filter.name}
								required />
						</div>
					}
					<div className="col-md-12 p-2">
						<label className="mb-1">{t("Standard.label")}</label>
						<select className="form-select"
							required={true}
							value={filter.reportStandard}
							onChange={(e) => setFilter({ ...filter, reportStandard: e.target.value })}>
							{filter.reportStandard === '' && (
								<option value='' key='' disabled>{t("Select.label")}</option>
							)}

							{view != 'GHG' && view != 'UAE_FDL' &&
								stdTypes?.map((item) => (
									<option value={item} key={item}>{item}</option>
								))
							}
							{view == 'GHG' &&
								<option value={'GHG'} key={'GHG'}>{'GHG'}</option>
							}
							{view == 'UAE_FDL' &&
								<option value={'UAE_FDL'} key={'UAE_FDL'}>{t('UAE_FDL.label')}</option>
							}
						</select>
					</div>
					{
						(filter.reportStandard === 'GRI')
							?
							<>
								<div className="col-md-12 p-2">
									<label className="mb-1">{t("PeriodStart.label")}</label>
									<ReportingPeriod
										t={t}
										defaultPeriod={filter.periodStart}
										defaultFrequency="MONTHLY"
										valueChangeCallback={(data) => {
											const periodStart = `${data.year}-${data.month.padStart(2, '0')}-01`;
											setFilter({ ...filter, periodStart });
										}}
									/>
								</div>
								<div className="col-md-12 p-2">
									<label className="mb-1">{t("PeriodEnd.label")}</label>
									<ReportingPeriod
										t={t}
										defaultPeriod={filter.periodEnd}
										defaultFrequency="MONTHLY"
										valueChangeCallback={(data) => {
											const lastDay = Utils.getLastDayOfMonth(data.year, data.month);
											const periodEnd = `${data.year}-${data.month.padStart(2, '0')}-${lastDay.toString().padStart(2, '0')}`;
											setFilter({ ...filter, periodEnd });
										}}
									/>
								</div>
							</>
							:
							<div className="col-md-12 p-2">
								<label className="mb-1">{t("ReportingPeriod.label")}</label>
								<select className="form-select"
									required={true}
									value={filter.year || currentYear}
									onChange={(e) => {
										const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(reportSettingsFreq, +e.target.value);
										setFilter({ 
											...filter, year: +e.target.value,
											periodStart: defaultPeriodStart,
											periodEnd: defaultPeriodEnd
										})
									}}>
									{options}
								</select>
							</div>
					}
					{!isGhgLikeReportStandard(filter.reportStandard) && filter.reportStandard !== 'ESRS' && filter.reportStandard !== 'VSME' && filter.reportStandard !== 'BRSR' && (
						<div className="col-md-12 p-2">
							<label className="mb-1">{t("Type.label")}</label>
							<select className="form-select"
								required={true}
								value={filter.basis}
								onChange={(e) => setFilter({ ...filter, basis: e.target.value, entities: [] })}>
								<option value={ReportBasis.ORG}>{t("OrganisationReport.label")}</option>
								<option value={ReportBasis.ENTITY}>{t("EntityReport.label")}</option>
							</select>
						</div>
					)}
					{filter.basis === ReportBasis.ENTITY && !isGhgLikeReportStandard(filter.reportStandard) && filter.reportStandard !== 'ESRS' && filter.reportStandard !== 'VSME' && filter.reportStandard !== 'BRSR' &&  (
						<div className="col-md-12 p-2">
							<label className="mb-1">{t("SelectEntities.label")}</label>
							<Typeahead
								id="entitySelector"
								multiple
								clearButton
								selected={branches.filter(branch => filter.entities.includes(branch.id))}
								labelKey={(option) => option.name}
								onChange={(selected) => {
									const entityIds = selected.map(entity => entity.id);
									setFilter({ ...filter, entities: entityIds });
								}}
								options={branches}
								placeholder={isLoadingBranches ? t("Loading.label") : t("SelectEntities.label")}
								disabled={isLoadingBranches}
								renderMenuItemChildren={(option) => (
									<div>
										<strong>{option.name}</strong>
									</div>
								)}
							/>
							<div className="mt-2">
								<button
									type="button"
									className="btn btn-sm btn-outline-primary"
									onClick={() => setFilter({ ...filter, entities: branches.map(branch => branch.id) })}
									disabled={isLoadingBranches}
								>
									{t("SelectAll.label")}
								</button>
							</div>
						</div>
					)}
					<div className="col-md-12 p-2">
						<label className="mb-1">{t("Language.label")}</label>
						<select className="form-select"
							required={true}
							value={filter.language}
							onChange={(e) => setFilter({ ...filter, language: e.target.value })}>
							{languageOptions.map((option) => (
								<option key={option.code} value={option.code}>
									{option.name}
								</option>
							))}
						</select>
					</div>
				</div>
			</Form>
		);
	};

	const fetchESRSMetricAndDiclosureData = () => {
		const payload = {
			reportId: activeReport.id,
			companyInfo: companyInfo,
			lang: i18n.language
		}
		setLoaderDisplay(true);
		ReportService.createJobAndUpdateESRSReport(payload).then(res => {
			if (res.status === 200) {
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				setPollerData({
					action: PollerAction.START,
					companyId: companyId,
					type: PollerType.REPORT_BUILDER,
					jobId: res.data.jobId,
					status: AIReportJobStatus.IN_PROGRESS,
					progress: '',
					jobType: 'AUTO_DRAFT',
					reportType: filter.reportStandard
				});
			}
			setLoaderDisplay(false);
			setShowConfirmation(false);
			exitModal();
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				if (currentRoute?.indexOf('/reporting_list') >= 0) {
					reportGenerationCallback();
				}
			}
		}).catch(err => {
			setLoaderDisplay(false);
			setShowConfirmation(false);
		});
	}

	const onConfirmAIReportGeneration = (confirmed) => {
		if (!confirmed) {
			setShowConfirmation(false);
			return;
		}
		if (filter.reportStandard === 'ESRS' || filter.reportStandard === 'VSME') {
			fetchESRSMetricAndDiclosureData();
		} else {
			fetchMetricAndDiclosureData();
		}

	};
	const navigateToAIConfig = () => {
		setnavAIShow(true)

	};
	const navigateToAIQual = () => {
		setQualShow(true);
	};
	const navigateToAIGD = () => {
		setGdShow(true);
	};
	const navigateToAIDataView = () => {
		setQuantshow(true)
	};

	const saveGRIReport = async () => {
		if (filter.name.trim() === '') {
			setValidated(true);
		} else {
			setValidated(false);
		}
		setLoaderDisplay(true);
		const report = {
			name: filter.name,
			status: ReportStatus.NOT_STARTED,
			sections: [],
			regulationId: filter.standardId,
			companyId: companyId,
			reportType: filter.reportStandard,
			lang: filter.language,
			period: filter.periodStart,
			periodEnd: filter.periodEnd,
			basis: filter.basis
		};

		// Add selected entities if Entity report is selected
		if (filter.basis === ReportBasis.ENTITY && filter.entities.length > 0) {
			report.entities = filter.entities;
		}

		let req;
		if (activeReport?.id > 0) {
			report.id = activeReport?.id;
			report.status = activeReport?.status;
			report.updateEssentialFieldsOnly = true;
			req = ReportService.updateReport(editedReport?.id, report);
		} else {
			const language = filter.language || i18n.language;
			// Ensure language resources are loaded before generating report sections
			await ensureLanguageLoaded(i18n, language);
			
			report.sections.push(ReportingUtils.getCoverSection(report, companyId, t, language, i18n));
			report.sections.push(ReportingUtils.getTableOfContentsSection(t, language, i18n));
			report.sections.push(ReportingUtils.getLeadershipSection(t, language, i18n));
			report.sections.push(ReportingUtils.getAboutReportSection(t, language, i18n));
			report.sections.push(ReportingUtils.getAboutCompanySection(t, language, i18n));
			report.sections.push(ReportingUtils.getOurESGApproachSection(t, language, i18n));
			report.sections.push(ReportingUtils.getEnvironmentSection(esgTopics?.filter(t => t.categoryType === CategoryType.ENVIRONMENTAL) ?? [], language, i18n));
			report.sections.push(ReportingUtils.getSocialSection(esgTopics?.filter(t => t.categoryType === CategoryType.SOCIAL) ?? [], language, i18n));
			report.sections.push(ReportingUtils.getGovernanceSection(esgTopics?.filter(t => t.categoryType === CategoryType.GOVERNANCE) ?? [], language, i18n));
			report.sections.push(ReportingUtils.getESGDisclosureSection(language, i18n));
			report.sections.push(ReportingUtils.getBackCover(null, language, i18n));
			report.sections?.forEach((section, sIndex) => {
				section.index = sIndex;
				section.sortOrder = sIndex + 1;
				section?.pages?.forEach((page, pIndex) => {
					page.index = pIndex;
					page.sortOrder = pIndex + 1;
				});
			});
			ReportingUtils.applyGriTableOfContentsToReport(report, t('TableofContents.label'));
			report.sections?.forEach(section => {
				section?.pages?.forEach((page, index) => {
					let trimmedHTML = page.html?.replace(/>\s+</g, '><');
					page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
					page.jsonContent = page.json ? JSON.stringify(page.json) : null;
					delete page.html;
					delete page.json;
					delete page.index;
				});
				delete section.index;
			});
			req = ReportService.createReport(report)
		}
		req.then(res => {
			if (res.status === 201 || res.status === 200) {
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
			setLoaderDisplay(false);
			setActiveReport({
				...res.data,
				job: activeReport.job,
			});
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				if (currentRoute?.indexOf('/reporting_list') >= 0) {
					reportGenerationCallback();
				}
			}
			getDataCompeltionProgress();
		}).catch(error => {
			console.error('[GRI Report] Report operation failed', { 
				reportId: activeReport?.id, 
				operation: activeReport?.id > 0 ? 'UPDATE' : 'CREATE',
				error: error, 
				timestamp: new Date().toISOString() 
			});
			
			// Handle specific error codes
			if (error.response?.data?.errorCode) {
				switch (error.response.data.errorCode) {
					case 'REPORT_NAME_EXISTS':
						toast.error('A GRI report with the same name already exists. Please choose a different name.', {
							position: toast.POSITION.TOP_RIGHT,
						});
						break;
					case 'MISSING_REQUIRED_FIELDS':
						toast.error('Please provide all required fields for GRI report.', {
							position: toast.POSITION.TOP_RIGHT,
						});
						break;
					case 'REPORT_NOT_FOUND':
						toast.error('GRI report not found.', {
							position: toast.POSITION.TOP_RIGHT,
						});
						break;
					case 'INVALID_REPORT_DATA':
						toast.error('Invalid GRI report data. Please check your inputs.', {
							position: toast.POSITION.TOP_RIGHT,
						});
						break;
					case 'REPORT_UPDATE_FAILED':
						toast.error('Failed to update GRI report. Please try again.', {
							position: toast.POSITION.TOP_RIGHT,
						});
						break;
					default:
						toast.error(error?.title || error?.message || "Failed to process GRI report. Please try again.", {
							position: toast.POSITION.TOP_RIGHT,
						});
						break;
				}
			} else {
				toast.error(error?.title || error?.message || "Failed to process GRI report. Please try again.", {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
			setLoaderDisplay(false);
		});
	};

	const saveGenericReport = () => {
		if (filter.name.trim() === '') {
			setValidated(true);
		} else {
			setValidated(false);
		}
		
		// Validate periodEnd date before proceeding
		if (filter.periodEnd) {
			const endDate = new Date(filter.periodEnd);
			if (isNaN(endDate.getTime())) {
				toast.error('Invalid periodEnd date provided. Please provide a valid date in YYYY-MM-DD format.', {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				return;
			}
			// Check if the date string matches the actual date (catches cases like 2025-04-31)
			const [year, month, day] = filter.periodEnd.split('-');
			if (endDate.getFullYear() !== parseInt(year) || 
				(endDate.getMonth() + 1) !== parseInt(month) || 
				endDate.getDate() !== parseInt(day)) {
				// Date is invalid (e.g., 2025-04-31 becomes 2025-05-01)
				toast.error(`Invalid periodEnd date provided: "${filter.periodEnd}". Please provide a valid date in YYYY-MM-DD format.`, {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				return;
			}
		}
		
		setLoaderDisplay(true);
		const report = {
			name: filter.name,
			status: ReportStatus.NOT_STARTED,
			sections: [],
			regulationId: filter.standardId,
			companyId: companyId,
			reportType: filter.reportStandard,
			lang: filter.language,
			period: filter.periodStart,
			periodEnd: filter.periodEnd,
			basis: filter.basis,
			frequency: reportSettingsFreq
		};

		// Add selected entities if Entity report is selected
		if (filter.basis === ReportBasis.ENTITY && filter.entities.length > 0) {
			report.entities = filter.entities;
		}
		let req;
		if (activeReport?.id > 0) {
			report.id = activeReport?.id;
			report.status = activeReport?.status;
			report.updateEssentialFieldsOnly = true;
			if (report.reportType == 'GHG') {
				req = ReportService.updateGHGReport(editedReport?.id, report);
			} else if (report.reportType == 'UAE_FDL') {
				req = ReportService.updateReport(editedReport?.id, report);
			} else {
				req = ReportService.updateESRSReport(editedReport?.id, report);
			}
		} else {
			if (report.reportType == 'GHG') {
				req = ReportService.createGHGReport(report);
			} else {
				req = ReportService.createESRSReport(report);
			}
		}
		req.then(res => {
			if (res.status === 201 || res.status === 200) {
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
			}
			setLoaderDisplay(false);
			setActiveReport({
				...res.data,
				job: activeReport.job,
			});
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				if (currentRoute?.indexOf('/reporting_list') >= 0) {
					reportGenerationCallback();
				}
			}
			getDataCompeltionProgress();
		}).catch(error => {
			setLoaderDisplay(false);
			// Handle specific error codes
			const errorData = error.response?.data;
			const errorMessage = errorData?.detail || errorData?.message || error.title || error.message;
			
			// Check if it's a date validation error
			if (errorMessage && (errorMessage.includes('Invalid periodEnd') || errorMessage.includes('invalid date') || errorMessage.includes('YYYY-MM-DD'))) {
				toast.error(errorMessage, {
					position: toast.POSITION.TOP_RIGHT,
				});
				return;
			}
			
			switch (errorData?.errorCode) {
				case 'REPORT_NAME_EXISTS':
					toast.error('A report with the same name already exists. Please choose a different name.', {
						position: toast.POSITION.TOP_RIGHT,
					});
					break;
				case 'MISSING_REQUIRED_FIELDS':
					toast.error('Please provide all required fields.', {
						position: toast.POSITION.TOP_RIGHT,
					});
					break;
				case 'REPORT_NOT_FOUND':
					toast.error('Report not found.', {
						position: toast.POSITION.TOP_RIGHT,
					});
					break;
				default:
					toast.error(errorMessage || 'An error occurred while saving the report.', {
						position: toast.POSITION.TOP_RIGHT,
					});
					break;
			}
		});
	};

	const saveBRSRReport = () => {
		if (filter.name.trim() === '') {
			setValidated(true);
			return;
		}
		setValidated(false);
		const selectedYear = filter.year || currentYear;
		const { defaultPeriodStart, defaultPeriodEnd  } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.FINANCIAL_YEAR, selectedYear);		
		setLoaderDisplay(true);
		const report = {
			name: filter.name,
			status: ReportStatus.NOT_STARTED,
			sections: [],
			regulationId: filter.standardId,
			companyId: companyId,
			reportType: 'BRSR',
			lang: filter.language,
			period: defaultPeriodStart,
			periodEnd: defaultPeriodEnd,
			basis: ReportBasis.ORG
		};
		let req;
		if (activeReport?.id > 0) {
			report.id = activeReport?.id;
			report.status = activeReport?.status;
			report.updateEssentialFieldsOnly = true;
			req = ReportService.updateBRSRReport(editedReport?.id, report);
		} else {
			req = ReportService.createBRSRReport(report);
		}		
		req.then(res => {
			if (res.status === 201 || res.status === 200) {
				toast.success("Success", {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				navigate('/company_admin/reporting?id=' + res.data.id
					+ '&mode=CREATE&regulationId=' + filter.standardId
					+ '&stdType=BRSR'
					+ '&year=' + selectedYear
					+ '&periodStart=' + defaultPeriodStart
					+ '&periodEnd=' + defaultPeriodEnd
					+ '&activeTab=' + activeTab,
					{ state: null });
				exitModal();
			}
		}).catch(error => {
			setLoaderDisplay(false);
			const errorData = error.response?.data;
			const errorMessage = errorData?.detail || errorData?.message || error.title || error.message;
			toast.error(errorMessage || 'An error occurred while saving the report.', {
				position: toast.POSITION.TOP_RIGHT,
			});
		});
	};

	const getDataCompeltionProgress = () => {
		setActiveCard(CardInDisplay.DATA_COMPLETION);
		setLoaderDisplay(true);
		const selectedYear = filter.year || currentYear;
		const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.CALENDAR_YEAR, selectedYear);
		const periodStart = filter.reportStandard === 'BRSR' ? defaultPeriodStart : filter.periodStart;
		const periodEnd = filter.reportStandard === 'BRSR' ? defaultPeriodEnd : filter.periodEnd;
		ReportService.getDataCompletionProgress(glbRegulationId, periodStart, false,
			periodStart, periodEnd, filter.entities
		).then(res => {
			setLoaderDisplay(false);
			if (res.status !== 200) return;

			setDataCompletionProgress(res.data.data);
		}).catch(error => {
			setLoaderDisplay(false);
		});
	};
	const renderGeneralDis = () => {
		return (
			<Modal show={true}
				aria-labelledby="contained-modal-title-vcenter"
				centered size="fullscreen" className="qual-modal" >
				<Modal.Header className="border-0 " >
					<Modal.Title as="h5">
						{gdshow && (t("GeneralDisclosure.label"))}
						{qualshow && (t("Qualitative.label"))}
						{quantshow && (t("Quantitative.label"))}
						{navAIShow && (<>Content Generation</>)}
					</Modal.Title>

					<Button variant="close" onClick={() => {
						{ gdshow && setGdShow(false) };
						{ quantshow && setQuantshow(false) }; { qualshow && setQualShow(false) }
						{ navAIShow && setnavAIShow(false) }
					}} aria-label="Close"></Button>

				</Modal.Header>
				<Modal.Body>
					{gdshow && <div className="ProfileSettings"><GeneralDisclosure activeView={activeView} embeddedView={'true'} yearFilter={filter.year} onDataUpdate={() => getDataCompeltionProgress()} /></div>}
					{qualshow && (<MaterialTopic embeddedView={'true'} yearFilter={filter.year} onDataUpdate={() => getDataCompeltionProgress()} />)}
					{quantshow && (<DataViewNew yearFilter={filter.year} onDataUpdate={() => getDataCompeltionProgress()} />)}
					{navAIShow && (<AIConfiguration />)}
				</Modal.Body>

			</Modal>
		);
	};

	const renderConfirmationModal = () => {

		return (
			<Modal show={true} onHide={() => {
				setShowConfirmation(false);
				setShowParentModal(true)
			}}
				aria-labelledby="contained-modal-title-vcenter"
				centered>
				<Modal.Header closeButton>
					<Modal.Title as="h5">
						{filter.name}
					</Modal.Title>

				</Modal.Header>
				<Modal.Body>
					<p><b>{uiElementsConfig?.texts?.esgAI ?? t('EcodrisilESGAI.label')} {t('AIReportConfirmation.label')}</b></p>
					<p className="d-flex align-items-baseline">
						<span>{t('AIReportConfirmationSubText.label')} <b>{aiPersonality}</b> </span>
						<span className="cursor ms-1 mb-0 text-primary" onClick={() => navigateToAIConfig()}> {t("clickhere.label")}</span>
					</p>
				</Modal.Body>
				<Modal.Footer>
					<Button variant="secondary" className="px-4" onClick={(e) => {
						setShowConfirmation(false)
						setShowParentModal(true)
					}} autoFocus>
						{t('Cancel.label')}
					</Button>
					<Button variant="primary" onClick={(e) => onConfirmAIReportGeneration(true)} className="px-4">
						{t('Generate.label')}
					</Button>
				</Modal.Footer>
			</Modal>
		);
	};

	const renderReportFormCard = () => {
		return (
			<div className="">
				{renderFilter()}
				{
					filter.reportStandard === 'BRSR-OLD'
						?
						<div className="pt-3 pb-2">
							<Button variant="primary" onClick={(e) => onNewReport()} className="px-4">
								{t('GenerateReport.label')}
							</Button>
						</div>
						:
						filter.reportStandard === 'ESRS' || filter.reportStandard === 'VSME' || isGhgLikeReportStandard(filter.reportStandard) ? <div className="pt-3 pb-2">
							<Button variant="primary" onClick={(e) => saveGenericReport()} className="px-4">
								{editedReport?.id > 0 ? t('Update.label') : t('Create.label')}
							</Button>
						</div> :
						filter.reportStandard === 'BRSR' ? <div className="pt-3 pb-2">
							<Button variant="primary" onClick={(e) => saveBRSRReport()} className="px-4">
								{editedReport?.id > 0 ? t('Update.label') : t('Create.label')}
							</Button>
						</div> :
							<div className="pt-3 pb-2">
								<Button variant="primary" onClick={(e) => saveGRIReport()} className="px-4">
									{editedReport?.id > 0 ? t('Update.label') : t('Create.label')}
								</Button>
							</div>
				}
			</div>
		);
	};



	const getColor = (percentage) => {
		if (percentage < 80) {
			return '#ef4838';  // Red
		} else if (percentage < 90) {
			return '#FFBF00';  // Yellow
		} else {
			return '#5abb6a';  // Green
		}
	};

	const renderDataCompletionInfo = (info) => {
		const mtPercentage = info?.find(p => p.inboundType === 'MT_QUESTION')?.percentage ?? 0;
		const gdPercentage = info?.find(p => p.inboundType === 'GD_QUESTION')?.percentage ?? 0;
		const dtPercentage = info?.find(p => p.inboundType === 'DATA_POINT')?.percentage ?? 0;
		return (
			<div className="p-0 mb-3">
				<div className="row m-0">
					<h6 className="">{t("Yourdatacompletionstatus.label")}</h6>

					<OverlayTrigger
						placement="bottom"
						overlay={<Tooltip id="tooltip-likee">{t("datashouldbecompleted.label")}</Tooltip>} >
						<div className="col-lg-4 p-2">
							<div className="card p-3 text-center DataCompletionStatusCard" onClick={() => navigateToAIDataView()}>
								<CircularProgressbar
									value={dtPercentage.toFixed(0)}
									text={`${dtPercentage.toFixed(0)}%`}
									styles={buildStyles({
										pathColor: getColor(dtPercentage),
										textColor: getColor(dtPercentage),
										trailColor: '#dee8ed'
									})}
								/>
								<h6>{t("QUANTITATIVE.label")}  </h6>

							</div>
						</div></OverlayTrigger>




					<OverlayTrigger
						placement="bottom"
						overlay={<Tooltip id="tooltip-like">{t("datashouldbecompleted.label")}</Tooltip>} >
						<div className="col-lg-4 p-2">
							<div className="card p-3 text-center DataCompletionStatusCard" onClick={() => navigateToAIQual()}>

								<CircularProgressbar
									value={mtPercentage.toFixed(0)}
									text={`${mtPercentage.toFixed(0)}%`}
									styles={buildStyles({
										pathColor: getColor(mtPercentage),
										textColor: getColor(mtPercentage),
										trailColor: '#dee8ed'
									})}
								/>
								<h6>{t("QUALITATIVE.label")} </h6>
							</div>
						</div>
					</OverlayTrigger>

					{filter.reportStandard === 'GRI' && <OverlayTrigger
						placement="bottom"
						overlay={<Tooltip id="tooltip-likee">{t("datashouldbecompleted.label")}</Tooltip>} >

						<div className="col-lg-4 p-2">
							<div className="card p-3 text-center DataCompletionStatusCard" onClick={() => navigateToAIGD()}>
								<CircularProgressbar
									value={gdPercentage.toFixed(0)}
									text={`${gdPercentage.toFixed(0)}%`}
									styles={buildStyles({
										pathColor: getColor(gdPercentage),
										textColor: getColor(gdPercentage),
										trailColor: '#dee8ed'
									})}
								/>
								<h6>{t("OneTime.label")} </h6>
							</div>
						</div>
					</OverlayTrigger>}



				</div>

			</div>
		);
	};

	const renderDataCompletionCard = () => {
		return (
			<div className="Datacompletion_statusbox 1">
				{renderDataCompletionInfo(dataCompletionProgress)}
				<p className="text-center">{t('DataCompletionStatusHelper2.label')}</p>
				<div className="text-center pt-2">
					<Button variant="secondary" onClick={(e) => {
						if (filter.reportStandard === 'BRSR') {
							const selectedYear = filter.year || currentYear;
							const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.FINANCIAL_YEAR, selectedYear);
							navigate('/company_admin/reporting?id=' + activeReport.id
								+ '&mode=EDIT&regulationId=' + filter.standardId
								+ '&stdType=BRSR'
								+ '&year=' + selectedYear
								+ '&periodStart=' + defaultPeriodStart
								+ '&periodEnd=' + defaultPeriodEnd
								+ '&activeTab=' + activeTab,
								{ state: null });
							exitModal();
						} else {
							exitModal();
						}
					}} className="px-4 me-2">
						{t('No.label')}
					</Button>
					<Button variant="primary" onClick={(e) => {
						if (filter.reportStandard === 'BRSR') {
							// For BRSR, navigate to mode=CREATE to let ReportingContainer regenerate the report
							const selectedYear = filter.year || currentYear;
							const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.FINANCIAL_YEAR, selectedYear);
							navigate('/company_admin/reporting?id=' + activeReport.id
								+ '&mode=CREATE&regulationId=' + filter.standardId
								+ '&stdType=BRSR'
								+ '&year=' + selectedYear
								+ '&periodStart=' + defaultPeriodStart
								+ '&periodEnd=' + defaultPeriodEnd
								+ '&activeTab=' + activeTab,
								{ state: null });
							exitModal();
						} else if (isGhgLikeReportStandard(filter.reportStandard)) {
							if (filter.reportStandard === 'GHG') {
								generateNewGHGCoDraftReport();
							} else {
								generateNewUAEFDLCoDraftReport();
							}
						} else {
							setActiveCard(CardInDisplay.TEMPLATE_CHOOSER);
						}
					}} className="px-4">
						{t('Yes.label')}
					</Button>
				</div>
			</div >
		);
	};
	const renderTemplateChooserCard = () => {
		return (
			<div className="Choose_templateBox">
				<p className="mb-3"></p>
				<div className="row m-0 justify-content-center">
					<div className="col-md-12 p-2">
						{
							pollerData.status !== AIReportJobStatus.IN_PROGRESS && pollerData.status !== AIReportJobStatus.NOT_STARTED
								?
								<div className="card report-card" onClick={() => onGenerateCoDraft()}>
									<div className="card-body p-2">
										<div className="row m-0  align-items-center">
											<div className=" col-md-3">
												<img src={scratch_icon} />
											</div>
											<div className="text-start col-md-9">
												<h6 className="fw-bold">{getCoDraftCardHeader()}</h6>
												<p className="mb-1">{getCoDraftCardDesc()}</p>
											</div>
										</div>
									</div>
								</div>
								:
								<div className="card disabled-card text-muted" onClick={() => null}>
									<div className="card-body p-2">
										<div className="row m-0  align-items-center">
											<div className=" col-md-3">
												<img src={scratch_icon} />
											</div>
											<div className="text-start col-md-9">
												<h6 className="fw-bold">{t('GeneratingReport.label')}</h6>
												<p>{t('GeneratingReportDesc.label')}</p>
											</div>
										</div>
									</div>
								</div>
						}

					</div>

					<div className="col-md-12 p-2">
						{
							pollerData.status !== AIReportJobStatus.IN_PROGRESS && pollerData.status !== AIReportJobStatus.NOT_STARTED
								?
								<div className="card report-card" onClick={() => onGenerateAIReport()}>
									<div className="card-body p-2">
										<div className="row m-0  align-items-center">
											<div className=" col-md-3">
												<img src={esg_ai_pic} />
											</div>
											<div className="text-start col-md-9">
												<h6 className="fw-bold">{getAutoDraftCardHeader()}</h6>
												<p className="m-0">{getAutoDraftCardDesc()}</p>
											</div>
										</div>
									</div>
								</div>
								:
								<div className="card disabled-card text-muted" onClick={() => null}>
									<div className="card-body">
										<div className="row m-0  align-items-center">
											<div className=" col-md-3">
												<img src={esg_ai_pic} />
											</div>
											<div className="text-start col-md-9">
												<h6 className="fw-bold">{t('GeneratingReport.label')}</h6>
												<p>{t('GeneratingReportDesc.label')}</p>
											</div>
										</div>
									</div>
								</div>
						}
					</div>
				</div>
			</div>
		);
	};

	const onGenerateCoDraft = () => {
		if (activeReport.job?.id > 0 && activeReport.status === 'DRAFT') {
			setActiveCard(CardInDisplay.REGENERATE_REPORT);
		} else {
			if (filter.reportStandard === 'ESRS') {
				generateNewCoDraftESRSReport();
			} else if (filter.reportStandard === 'VSME') {
				generateNewVSMECoDraftReport();
			} else if (filter.reportStandard === 'GHG') {
				generateNewGHGCoDraftReport();
			} else if (filter.reportStandard === 'UAE_FDL') {
				generateNewUAEFDLCoDraftReport();
			} else {
				generateNewCoDraftReport();
			}
		}
	};

	const generateNewCoDraftReport = () => {
		setActiveCard(CardInDisplay.REPORT_GEN_LOADER);
		const req1 = ReportService.createCoDraftJob(activeReport.id);
		const req2 = ReportDataService.getESGSummary({
			standardId: filter.standardId,
			periodStart: filter.periodStart,
			periodEnd: filter.periodEnd,
			buIds: filter.entities
		});
		Promise.all([req1, req2]).then(res => {
			const coDraftJobId = res[0].data.jobId;
			const esgDataRes = res[1].data;
			if (!coDraftJobId || !esgDataRes) {
				reportGenerationFailed();
				return;
			}
			let payload = ReportingUtils.mapESGSummaryDataToAIPayload(esgDataRes);
			payload.job_id = coDraftJobId;
			payload.report_type = activeReport.basis ?? ReportBasis.ORG;
			payload.company = {
				company_id: companyId,
				company_info: companyInfo?.about ?? '',
				company_name: lsData.profile.company.legalName,
				country: companyInfo?.address?.country?.name ?? '',
				esg_frameworks: companyInfo?.esgFrameworks ?? '',
				esg_goals: companyInfo?.esgGoals ?? '',
				industry: companyInfo?.industry?.name ?? '',
				reporting_standard: 'GRI',
				reporting_year: filter.periodStart && filter.periodEnd
					? ReportingUtils.formatPeriodForAIPayload(filter.periodStart, filter.periodEnd) : `${filter.periodStart.split('-')[0]}`,
			};
			payload.language = preference.language === 'en' ? 'en-us' : preference.language
			ReportAIService.generateTextForESGHighlights(payload)
				.then(res => {
					setPollerData({
						action: PollerAction.START,
						companyId: companyId,
						type: PollerType.REPORT_BUILDER,
						jobId: coDraftJobId,
						status: AIReportJobStatus.IN_PROGRESS,
						progress: '',
						jobType: 'CO_DRAFT',
						reportData: {
							id: activeReport.id,
							standardId: filter.standardId,
							reportStandard: filter.reportStandard,
							year: filter.year,
							jobId: coDraftJobId,
							esgData: esgDataRes
						}
					});
				})
				.catch(err => {
					reportGenerationFailed();
					ReportService.updateJobStatus(coDraftJobId, 3).then(res => {
						if (res.status !== 200) return;

					}).catch(err => { });
					exitModal();
				});
			updateMetricAndDisclosureDataIntheReport();
		}).catch(err => {
			reportGenerationFailed();
		});
	};

	const generateNewCoDraftESRSReport = () => {
		setLoaderDisplay(true);
		const report = {
			id: activeReport.id,
			status: ReportStatus.DRAFT,
			sections: [],
			lang: filter.language
		}
		const page1Req = ReportService.getReportV2Template({ pageId: 1, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page2Req = ReportService.getReportV2Template({ pageId: 2, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page3Req = ReportService.getReportV2Template({ pageId: 3, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page4Req = ReportService.getReportV2Template({ pageId: 4, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page5Req = ReportService.getReportV2Template({ pageId: 5, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page6Req = ReportService.getReportV2Template({ pageId: 6, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page7Req = ReportService.getReportV2Template({ pageId: 7, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page8Req = ReportService.getReportV2Template({ pageId: 8, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page9Req = ReportService.getReportV2Template({ pageId: 9, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq  })
		const page10Req = ReportService.getReportV2Template({ pageId: 10, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page11Req = ReportService.getReportV2Template({ pageId: 11, type: 'ESRS', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const doubleMatReportReq = MatAssessmentService.getLatestReport(filter.periodStart.split('-')[0]);
		Promise.all([
			page1Req,
			page2Req,
			page3Req,
			page4Req,
			page5Req,
			page6Req,
			page7Req,
			page8Req,
			page9Req,
			page10Req,
			page11Req,
			doubleMatReportReq
		]).then(async (res) => {
			// Ensure language resources are loaded before generating report sections
			await ensureLanguageLoaded(i18n, filter.language);
			
			const e1Data = !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const e2Data = !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const e3Data = !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const e4Data = !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const e5Data = !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const s1Data = !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const s2Data = !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const s3Data = !!res[7].data.data ? res[7].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const s4Data = !!res[8].data.data ? res[8].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const g1Data = !!res[9].data.data ? res[9].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const d1Data = !!res[10].data.data ? res[10].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const doubleMatReportData = res[11].data;
			// Prepare report metadata for ESRS
			const esrsReportMetadata = {
				version: "1",
				generatedBy: lsData.profile.company.legalName,
				date: new Date().toLocaleDateString('en-US', { 
					year: 'numeric', 
					month: '2-digit', 
					day: '2-digit' 
				}).replace(/\//g, '-'),
				companyName: lsData.profile.company.legalName
			};
			const language = filter.language;
			report.sections.push(ESRSReportingUtils.getCoverSection(companyId, filter.periodStart.split('-')[0], t, esrsReportMetadata, language, i18n));
			report.sections.push(ESRSReportingUtils.getTableOfContentsSection(t, language, i18n));
			report.sections.push(ESRSReportingUtils.getLeadershipSection(t, language, i18n));
			report.sections.push(ESRSReportingUtils.getAboutReportSection(t, language, i18n));
			report.sections.push(ESRSReportingUtils.getAboutCompanySection(t, language, i18n));
			report.sections.push(ESRSReportingUtils.getOurESGApproachSection(t, language, i18n));
			report.sections.push(ESRSReportingUtils.getGeneralDisclosureSection({
				disclosureData: d1Data,
				doubleMatReport: doubleMatReportData,
				language: language,
				t: t,
				i18n: i18n
			}));
			report.sections.push(ESRSReportingUtils.getEnvironmentSection(e1Data, e2Data, e3Data, e4Data, e5Data, language, t, i18n));
			report.sections.push(ESRSReportingUtils.getSocialSection(s1Data, s2Data, s3Data, s4Data, language, t, i18n));
			report.sections.push(ESRSReportingUtils.getGovernanceSection(g1Data, language, t, i18n));
			report.sections.push(ESRSReportingUtils.getBackCover(companyId, filter.periodStart.split('-')[0], t, language, i18n));
			report.sections?.forEach(section => {
				section?.pages?.forEach(page => {
					let trimmedHTML = page.html?.replace(/>\s+</g, '><');
					page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
					page.jsonContent = page.json ? JSON.stringify(page.json) : null;
					delete page.html;
					delete page.json;
					delete page.index;
				});
				delete section.index;
			});
			ReportService.updateESRSReport(activeReport.id, report).then(res => {
				if (res.status === 201 || res.status === 200) {
					toast.success("Success", {
						position: toast.POSITION.TOP_RIGHT,
					});
					setLoaderDisplay(false);
					navigate('/company_admin/reporting?id=' + activeReport.id
						+ '&mode=EDIT&regulationId=' + filter.standardId
						+ '&stdType=' + filter.reportStandard
						+ '&periodStart=' + filter.periodStart
						+ '&periodEnd=' + filter.periodEnd
						+ '&jobId=' + activeReport.job?.id
						+ '&activeTab=' + activeTab,
						{ state: null });
					exitModal();
				}
			}).catch(error => {
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				exitModal();
			});
		}).catch(err => {
			toast.error("Error fetching template data", {
				position: toast.POSITION.TOP_RIGHT,
			});
			const req = ReportService.failedESRSReport(activeReport.id);
			setLoaderDisplay(false);
			exitModal();
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				reportGenerationCallback();
			}
		});
	};
	const reportGenerationFailed = () => {
		toast.error("Failed to generate the report", {
			position: toast.POSITION.TOP_RIGHT,
		});
		exitModal();
	};

	const renderRegenerateReportCard = () => {
		return (
			<div className="Datacompletion_statusbox 2">
				{
					activeReport?.status === 'DRAFT' &&
					activeReport?.dataCompletionProgress &&
					renderDataCompletionInfo(activeReport?.dataCompletionProgress)
				}
				<p className="text-center">{t('ReportDataChanged.label')}</p>
				<div className="text-center pt-2">

					<Button variant="secondary" onClick={(e) => {
						if (filter.reportStandard === 'BRSR') {
							const selectedYear = filter.year || currentYear;
							const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.FINANCIAL_YEAR, selectedYear);
							navigate('/company_admin/reporting?id=' + activeReport.id
								+ '&mode=EDIT&regulationId=' + filter.standardId
								+ '&stdType=BRSR'
								+ '&year=' + selectedYear
								+ '&periodStart=' + defaultPeriodStart
								+ '&periodEnd=' + defaultPeriodEnd
								+ '&jobId=' + activeReport.job?.id
								+ '&activeTab=' + activeTab,
								{ state: null });
						} else {
							navigate('/company_admin/reporting?id=' + activeReport.id
								+ '&mode=EDIT&regulationId=' + filter.standardId
								+ '&stdType=' + filter.reportStandard
								+ '&year=' + filter.periodStart.split('-')[0]
								+ '&periodStart=' + filter.periodStart
								+ '&periodEnd=' + filter.periodEnd
								+ '&entities=' + filter.entities.join(',')
								+ '&jobId=' + activeReport.job?.id
								+ '&activeTab=' + activeTab,
								{ state: null });
						}
					}} className="px-4 me-2">
						{t('No.label')}
					</Button>
					<Button variant="primary" onClick={(e) => {
						if (filter.reportStandard === 'ESRS') {
							generateNewCoDraftESRSReport();
						} else if (filter.reportStandard === 'VSME') {
							generateNewVSMECoDraftReport()
						} else if (filter.reportStandard === 'GHG') {
							generateNewGHGCoDraftReport()
						} else if (filter.reportStandard === 'UAE_FDL') {
							generateNewUAEFDLCoDraftReport()
						} else if (filter.reportStandard === 'BRSR') {
							// For BRSR, navigate to mode=CREATE to let ReportingContainer regenerate the report
							const selectedYear = filter.year || currentYear;
							const { defaultPeriodStart, defaultPeriodEnd } = Utils.calculateDefaultPeriods(ReportSettingsFrequency.FINANCIAL_YEAR, selectedYear);
							navigate('/company_admin/reporting?id=' + activeReport.id
								+ '&mode=CREATE&regulationId=' + filter.standardId
								+ '&stdType=BRSR'
								+ '&year=' + selectedYear
								+ '&periodStart=' + defaultPeriodStart
								+ '&periodEnd=' + defaultPeriodEnd
								+ '&activeTab=' + activeTab,
								{ state: null });
							exitModal();
						} else {
							generateNewCoDraftReport();
						}
					}} className="px-4">
						{t('Yes.label')}
					</Button>
				</div>
			</div>
		);
	};

	const updateMetricAndDisclosureDataIntheReport = () => {
		const lang = (filter.language == 'en-us' || filter.language == 'en-uk') ? 'en' : filter.language;
		const metricReq = StandardService.getMetricData({
			stdId: filter.standardId,
			period: filter.periodStart,
			standardTypes: [filter.reportStandard, 'ESRS'],
			language: lang,
			periodStart: filter.periodStart,
			periodEnd: filter.periodEnd,
		});
		const discReq = GeneralDisclosureService.getQuestionsAndAnswers({
			stdType: filter.reportStandard,
			period: filter.periodStart,
			lang: lang,
			periodStart: filter.periodStart, periodEnd: filter.periodEnd,
		});
		setReportDataUpdateInProgress(true);
		Promise.all([metricReq, discReq]).then(res => {
			const metricData = res[0].data;
			const discData = res[1].data.data;
			const report = {
				id: activeReport.id,
				name: activeReport.name,
				status: ReportStatus.NOT_STARTED,
				sections: [],
				regulationId: filter.standardId,
				companyId: companyId,
				basis: filter.basis
			};

			// Add selected entities if Entity report is selected
			if (filter.basis === ReportBasis.ENTITY && filter.entities.length > 0) {
				report.entities = filter.entities.map(entity => entity.id);
			}
			report.sections.push(ReportingUtils.getESGDisclosureSection(lang, i18n));
			// Set data for both gri and content index pages
			report.sections[0].pages[0].json = metricData;
			report.sections[0].pages[1].json = metricData;
			const questions = discData?.questions ?? [];
			// Get page html for gri index page
			let pageHTML = report.sections[0].pages[0].html;
			let renderedMetricHTML = renderToStaticMarkup(
				<ReportingStandardData
					page={report.sections[0].pages[0]}
					disclosureQuestions={questions}
					periodStart={filter.periodStart}
					periodEnd={filter.periodEnd} />
			);
			let formattedHTML = `${pageHTML}${renderedMetricHTML}`;
			report.sections[0].pages[0].html = formattedHTML;
			// Get page html for content index page
			pageHTML = report.sections[0].pages[1].html;
			renderedMetricHTML = renderToStaticMarkup(
				<ReportingStandardData
					page={report.sections[0].pages[1]}
					disclosureQuestions={[]}
					periodStart={filter.periodStart}
					periodEnd={filter.periodEnd} />
			);
			formattedHTML = `${pageHTML}${renderedMetricHTML}`;
			report.sections[0].pages[1].html = formattedHTML;
			report.sections?.forEach(section => {
				section?.pages?.forEach((page, index) => {
					let trimmedHTML = page.html?.replace(/>\s+</g, '><');
					page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
					page.jsonContent = page.json ? JSON.stringify(page.json) : null;
					delete page.html;
					delete page.json;
					delete page.index;
				});
				delete section.index;
			});
			report.period = filter.periodStart;
			report.periodEnd = filter.periodEnd;
			report.updateESGIndexPageOnly = true;
			report.lang = filter.language;
			report.basis = filter.basis;
			// Add selected entities if Entity report is selected
			if (filter.basis === ReportBasis.ENTITY && filter.entities.length > 0) {
				report.entities = filter.entities.map(entity => entity.id);
			}
			ReportService.updateReport(activeReport.id, report).then(res => {
				setReportDataUpdateInProgress(false);
			}).catch(error => {
				setReportDataUpdateInProgress(false);
			});
		}).catch(err => {
			setReportDataUpdateInProgress(false);
		});
	};

	const fetchESGTopics = () => {
		const lang = (filter.language == 'en-us' || filter.language == 'en-uk') ? 'en' : filter.language;
		StandardService.getESGTopics(glbRegulationId, lang).then(res => {
			if (res.status !== 200) return;

			setESGTopics(res.data ?? []);
		}).catch(err => { });
	}

	const getCoDraftCardHeader = () => {
		if (filter.reportStandard == 'ESRS') {
			return <>{t('BasicESRSReportHeader.label')}</>;
		} else if (filter.reportStandard == 'VSME') {
			return <>{t('BasicVSMEReportHeader.label')}</>;
		} else {
			return <>{t('BasicReportHeader.label')}</>;
		}
	};

	const getCoDraftCardDesc = () => {
		if (filter.reportStandard == 'ESRS') {
			return <>{t('ScratchESRSReportDesc1.label')}</>;
		} else if (filter.reportStandard == 'VSME') {
			return <>{t('ScratchVSMEReportDesc1.label')}</>;
		} else {
			return <>{t('ScratchReportDesc1.label')}</>;
		}
	};

	const getAutoDraftCardHeader = () => {
		if (filter.reportStandard == 'ESRS') {
			return <>{t('BuildESRSWithAIHeader.label')}</>;
		} else if (filter.reportStandard == 'VSME') {
			return <>{t('BuildVSMEWithAIHeader.label')}</>;
		} else {
			return <>{t('BuildWithAIHeader.label')}</>;
		}
	};

	const getAutoDraftCardDesc = () => {
		if (filter.reportStandard == 'ESRS') {
			return <>{t('AIReportESRSWithDesc1.label')}</>;
		} else if (filter.reportStandard == 'VSME') {
			return <>{t('AIReportVSMEWithDesc1.label')}</>;
		} else {
			return <>{t('AIReportWithDesc1.label')}</>;
		}
	};

	const generateNewVSMECoDraftReport = () => {
		setLoaderDisplay(true);
		const report = {
			id: activeReport.id,
			status: ReportStatus.DRAFT,
			sections: [],
			lang: filter.language
		}
		const page1Req = ReportService.getReportV2Template({ pageId: 1, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page2Req = ReportService.getReportV2Template({ pageId: 2, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page3Req = ReportService.getReportV2Template({ pageId: 3, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page4Req = ReportService.getReportV2Template({ pageId: 4, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page5Req = ReportService.getReportV2Template({ pageId: 5, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page6Req = ReportService.getReportV2Template({ pageId: 6, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page7Req = ReportService.getReportV2Template({ pageId: 7, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page8Req = ReportService.getReportV2Template({ pageId: 8, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page9Req = ReportService.getReportV2Template({ pageId: 9, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page10Req = ReportService.getReportV2Template({ pageId: 10, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page11Req = ReportService.getReportV2Template({ pageId: 11, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page12Req = ReportService.getReportV2Template({ pageId: 12, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page13Req = ReportService.getReportV2Template({ pageId: 13, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page14Req = ReportService.getReportV2Template({ pageId: 14, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page15Req = ReportService.getReportV2Template({ pageId: 15, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page16Req = ReportService.getReportV2Template({ pageId: 16, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page17Req = ReportService.getReportV2Template({ pageId: 17, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page18Req = ReportService.getReportV2Template({ pageId: 18, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page19Req = ReportService.getReportV2Template({ pageId: 19, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page20Req = ReportService.getReportV2Template({ pageId: 20, type: 'VSME', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		Promise.all([
			page1Req,
			page2Req,
			page3Req,
			page4Req,
			page5Req,
			page6Req,
			page7Req,
			page8Req,
			page9Req,
			page10Req,
			page11Req,
			page12Req,
			page13Req,
			page14Req,
			page15Req,
			page16Req,
			page17Req,
			page18Req,
			page19Req,
			page20Req,
		]).then(async (res) => {
			// Ensure language resources are loaded before generating report sections
			await ensureLanguageLoaded(i18n, filter.language);
			
			const b1Data = !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b2Data = !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b3Data = !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b4Data = !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b5Data = !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b6Data = !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b7Data = !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b8Data = !!res[7].data.data ? res[7].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b9Data = !!res[8].data.data ? res[8].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b10Data = !!res[9].data.data ? res[9].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b11Data = !!res[10].data.data ? res[10].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c1 = !!res[11].data.data ? res[11].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c2 = !!res[12].data.data ? res[12].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c3 = !!res[13].data.data ? res[13].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c4 = !!res[14].data.data ? res[14].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c5 = !!res[15].data.data ? res[15].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c6 = !!res[16].data.data ? res[16].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c7 = !!res[17].data.data ? res[17].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c8 = !!res[18].data.data ? res[18].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const c9 = !!res[19].data.data ? res[19].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			// Prepare report metadata for VSME
			const vsmeReportMetadata = {
				version: "1",
				generatedBy: lsData.profile.company.legalName,
				date: new Date().toLocaleDateString('en-US', { 
					year: 'numeric', 
					month: '2-digit', 
					day: '2-digit' 
				}).replace(/\//g, '-'),
				companyName: lsData.profile.company.legalName
			};
			const language = filter.language;
			report.sections.push(VSMEReportingUtils.getCoverSection(companyId, filter.periodStart.split('-')[0], t, vsmeReportMetadata, language, i18n));
			report.sections.push(VSMEReportingUtils.getTableOfContentsSection(t, language, i18n));
			report.sections.push(VSMEReportingUtils.getLeadershipSection(t, language, i18n));
			report.sections.push(VSMEReportingUtils.getAboutReportSection(t, language, i18n));
			report.sections.push(VSMEReportingUtils.getAboutCompanySection(t, language, i18n));
			report.sections.push(VSMEReportingUtils.getOurESGApproachSection(t, language, i18n));
			report.sections.push(VSMEReportingUtils.getGeneralInfoSection(b1Data, b2Data, c1, c2, language, t, i18n));
			report.sections.push(VSMEReportingUtils.getEnvironmentSection(b3Data, b4Data, b5Data, b6Data, b7Data, c3, c4, language, t, i18n));
			report.sections.push(VSMEReportingUtils.getSocialSection(b8Data, b9Data, b10Data, c5, c6, c7, language, t, i18n));
			report.sections.push(VSMEReportingUtils.getGovernanceSection(b11Data, c8, c9, language, t, i18n));
			report.sections.push(VSMEReportingUtils.getBackCover(companyId, filter.periodStart.split('-')[0], t, language, i18n));
			report.sections?.forEach(section => {
				section?.pages?.forEach(page => {
					let trimmedHTML = page.html?.replace(/>\s+</g, '><');
					page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
					page.jsonContent = page.json ? JSON.stringify(page.json) : null;
					delete page.html;
					delete page.json;
					delete page.index;
				});
				delete section.index;
			});
			ReportService.updateESRSReport(activeReport.id, report).then(res => {
				if (res.status === 201 || res.status === 200) {
					toast.success("Success", {
						position: toast.POSITION.TOP_RIGHT,
					});
					setLoaderDisplay(false);
					navigate('/company_admin/reporting?id=' + activeReport.id
						+ '&mode=EDIT&regulationId=' + filter.standardId
						+ '&stdType=' + filter.reportStandard
						+ '&year=' + filter.periodStart.split('-')[0]
						+ '&periodStart=' + filter.periodStart
						+ '&periodEnd=' + filter.periodEnd
						+ '&jobId=' + activeReport.job?.id
						+ '&activeTab=' + activeTab,
						{ state: null });
					exitModal();
				}
			}).catch(error => {
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				exitModal();
			});
		}).catch(err => {
			toast.error("Error fetching template data", {
				position: toast.POSITION.TOP_RIGHT,
			});
			const req = ReportService.failedESRSReport(activeReport.id);
			setLoaderDisplay(false);
			exitModal();
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				reportGenerationCallback();
			}
		});
	};

	const generateNewGHGCoDraftReport = () => {
		setLoaderDisplay(true);
		const report = {
			id: activeReport.id,
			name: activeReport.name,
			status: ReportStatus.DRAFT,
			sections: [],
			regulationId: filter.standardId,
			companyId: companyId,
			basis: filter.basis,
			entities: filter.entities,
			lang: filter.language
		}
		const page1Req = ReportService.getReportV2Template({ pageId: 1, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page2Req = ReportService.getReportV2Template({ pageId: 2, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page3Req = ReportService.getReportV2Template({ pageId: 3, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page4Req = ReportService.getReportV2Template({ pageId: 4, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page5Req = ReportService.getReportV2Template({ pageId: 5, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page6Req = ReportService.getReportV2Template({ pageId: 6, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page7Req = ReportService.getReportV2Template({ pageId: 7, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page8Req = ReportService.getReportV2Template({ pageId: 8, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		const page9Req = ReportService.getReportV2Template({ pageId: 9, type: 'GHG', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq })
		Promise.all([
			page1Req,
			page2Req,
			page3Req,
			page4Req,
			page5Req,
			page6Req,
			page7Req,
			page8Req,
			page9Req
		]).then(async (res) => {
			// Ensure language resources are loaded before generating report sections
			await ensureLanguageLoaded(i18n, filter.language);
			
			const b1Data = !!res[0].data.data ? res[0].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b2Data = !!res[1].data.data ? res[1].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b3Data = !!res[2].data.data ? res[2].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b4Data = !!res[3].data.data ? res[3].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b5Data = !!res[4].data.data ? res[4].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b6Data = !!res[5].data.data ? res[5].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b7Data = !!res[6].data.data ? res[6].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b8Data = !!res[7].data.data ? res[7].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			const b9Data = !!res[8].data.data ? res[8].data.data.sort((a, b) => a.sortOrder - b.sortOrder) : [];
			// Prepare report metadata
			const reportMetadata = {
				version: "1",
				generatedBy: lsData.profile.company.legalName,
				date: new Date().toLocaleDateString('en-US', { 
					year: 'numeric', 
					month: '2-digit', 
					day: '2-digit' 
				}).replace(/\//g, '-'),
				companyName: lsData.profile.company.legalName
			};
			report.sections.push(GHGReportingUtils.getCoverSection(companyId, filter.periodStart.split('-')[0], t, reportMetadata, filter.language, i18n));
			report.sections.push(GHGReportingUtils.getTableOfContentsSection(t, filter.language, i18n));

			report.sections.push(GHGReportingUtils.getExecutiveSummarySection(b1Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getAboutReportSection(b2Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getOrganizationalProfileSection(b3Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getBaseYearMethodologySection(b4Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getGHGEmissionsInventorySection(b5Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getEnergyUseEmissionSourcesSection(b6Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getGHGReductionStrategySection(b7Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getVerificationAssuranceSection(b8Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getInsightsSection([], filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));
			report.sections.push(GHGReportingUtils.getAnnexuresSection(b9Data, filter.language, t, i18n, {decimalPlaces: companyInfo.decimalPlaces}));

			// report.sections.push(GHGReportingUtils.getBackCover(companyId, filter.periodStart.split('-')[0], t));
			report.sections?.forEach(section => {
				section?.pages?.forEach(page => {
					let trimmedHTML = page.html?.replace(/>\s+</g, '><');
					page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
					page.jsonContent = page.json ? JSON.stringify(page.json) : null;
					delete page.html;
					delete page.json;
					delete page.index;
				});
				delete section.index;
			});
			ReportService.updateGHGReport(activeReport.id, report).then(res => {
				if (res.status === 201 || res.status === 200) {
					toast.success("Success", {
						position: toast.POSITION.TOP_RIGHT,
					});
					setLoaderDisplay(false);
					navigate('/company_admin/reporting?id=' + activeReport.id
						+ '&mode=EDIT&regulationId=' + filter.standardId
						+ '&stdType=' + filter.reportStandard
						+ '&year=' + filter.periodStart.split('-')[0]
						+ '&periodStart=' + filter.periodStart
						+ '&periodEnd=' + filter.periodEnd
						+ '&jobId=' + activeReport.job?.id
						+ '&activeTab=' + activeTab,
						{ state: null });
					exitModal();
				}
			}).catch(error => {
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				exitModal();
			});
		}).catch(err => {
			toast.error("Error fetching template data", {
				position: toast.POSITION.TOP_RIGHT,
			});
			const req = ReportService.failedESRSReport(activeReport.id);
			setLoaderDisplay(false);
			exitModal();
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				reportGenerationCallback();
			}
		});
	};

	const generateNewUAEFDLCoDraftReport = () => {
		setLoaderDisplay(true);
		const report = {
			id: activeReport.id,
			name: activeReport.name,
			status: ReportStatus.DRAFT,
			sections: [],
			regulationId: filter.standardId,
			companyId: companyId,
			basis: filter.basis,
			entities: filter.entities,
			lang: filter.language
		};
		const templateParams = { type: 'UAE_FDL', standardId: glbRegulationId, period: filter.periodStart, periodEnd: filter.periodEnd, rFreq: reportSettingsFreq };
		const pageReqs = [1, 2, 3, 4, 5, 6, 7].map((pageId) =>
			ReportService.getReportV2Template({ pageId, ...templateParams })
		);
		Promise.all(pageReqs).then(async (res) => {
			await ensureLanguageLoaded(i18n, filter.language);

			const pageData = res.map((r) => (r.data.data ? r.data.data.sort((a, b) => a.sortOrder - b.sortOrder) : []));
			const reportMetadata = {
				version: "1",
				generatedBy: lsData.profile.company.legalName,
				date: new Date().toLocaleDateString('en-US', {
					year: 'numeric',
					month: '2-digit',
					day: '2-digit'
				}).replace(/\//g, '-'),
				companyName: lsData.profile.company.legalName
			};
			const sectionSettings = { decimalPlaces: companyInfo.decimalPlaces };

			report.sections.push(UAEFDLReportingUtils.getCoverSection(companyId, filter.periodStart.split('-')[0], t, reportMetadata, filter.language, i18n));
			report.sections.push(UAEFDLReportingUtils.getTableOfContentsSection(t, filter.language, i18n));
			report.sections.push(UAEFDLReportingUtils.getAboutTheOrganizationSection(pageData[0], filter.language, t, i18n, sectionSettings));
			report.sections.push(UAEFDLReportingUtils.getAboutTheReportSection(pageData[1], filter.language, t, i18n, sectionSettings));
			report.sections.push(UAEFDLReportingUtils.getInventoryOfGHGEmissionsSection(pageData[2], filter.language, t, i18n, sectionSettings));
			report.sections.push(UAEFDLReportingUtils.getInformationAboutEmissionsSection(pageData[3], filter.language, t, i18n, sectionSettings));
			report.sections.push(UAEFDLReportingUtils.getDecarbonizationStrategySection(pageData[4], filter.language, t, i18n, sectionSettings));
			report.sections.push(UAEFDLReportingUtils.getVerificationComplianceSection(pageData[5], filter.language, t, i18n, sectionSettings));
			report.sections.push(UAEFDLReportingUtils.getInsightsSection(pageData[6] || [], filter.language, t, i18n, sectionSettings));

			report.sections?.forEach(section => {
				section?.pages?.forEach(page => {
					let trimmedHTML = page.html?.replace(/>\s+</g, '><');
					page.htmlContent = trimmedHTML ? window.btoa(unescape(encodeURIComponent(trimmedHTML))) : null;
					page.jsonContent = page.json ? JSON.stringify(page.json) : null;
					delete page.html;
					delete page.json;
					delete page.index;
				});
				delete section.index;
			});
			ReportService.updateReport(activeReport.id, report).then(res => {
				if (res.status === 201 || res.status === 200) {
					toast.success("Success", {
						position: toast.POSITION.TOP_RIGHT,
					});
					setLoaderDisplay(false);
					navigate('/company_admin/reporting?id=' + activeReport.id
						+ '&mode=EDIT&regulationId=' + filter.standardId
						+ '&stdType=' + filter.reportStandard
						+ '&year=' + filter.periodStart.split('-')[0]
						+ '&periodStart=' + filter.periodStart
						+ '&periodEnd=' + filter.periodEnd
						+ '&jobId=' + activeReport.job?.id
						+ '&activeTab=' + activeTab,
						{ state: null });
					exitModal();
				}
			}).catch(error => {
				toast.error(error.title, {
					position: toast.POSITION.TOP_RIGHT,
				});
				setLoaderDisplay(false);
				exitModal();
			});
		}).catch(err => {
			toast.error("Error fetching template data", {
				position: toast.POSITION.TOP_RIGHT,
			});
			ReportService.updateReportStatus(activeReport.id, ReportStatus.FAILED);
			setLoaderDisplay(false);
			exitModal();
			if (currentRoute?.indexOf('/reporting_list') >= 0) {
				reportGenerationCallback();
			}
		});
	};


	return (
		<>
			<Modal
				show={showParentModal}
				onHide={() => {
					setShow(false);
					onHide();
				}}
				size={activeCard === CardInDisplay.REPORT_GEN_LOADER ? 'xl' : 'md'}
				aria-labelledby="contained-modal-title-vcenter"
				className="Create_New_ReportModal"
				centered
				backdrop={activeCard === CardInDisplay.REPORT_GEN_LOADER ? 'static' : true}>
				<Modal.Header className="border-0" closeButton={activeCard !== CardInDisplay.REPORT_GEN_LOADER}>
					{activeCard === CardInDisplay.REPORT_FORM && (
						<Modal.Title bsPrefix="h5 1">
							{editedReport?.id > 0 ? t('UpdateReport.label') : t('CreateNewReport.label')}
						</Modal.Title>
					)}
					{activeCard === CardInDisplay.DATA_COMPLETION && (
						<Modal.Title bsPrefix="h5 2">
							{filter.name}
						</Modal.Title>
					)}
					{activeCard === CardInDisplay.TEMPLATE_CHOOSER && (
						<Modal.Title bsPrefix="h5 3">
							{filter.name}
						</Modal.Title>
					)}
					{activeCard === CardInDisplay.REGENERATE_REPORT && (
						<Modal.Title as="h5">
							{filter.name}
						</Modal.Title>
					)}
					{activeCard === CardInDisplay.REPORT_GEN_LOADER && (
						<Modal.Title bsPrefix="h5 5">
							{filter.name}
						</Modal.Title>
					)}
				</Modal.Header>
				{/* )} */}
				<Modal.Body>
					<div className="new-report-modal">
						{activeCard === CardInDisplay.REPORT_FORM && renderReportFormCard()}
						{activeCard === CardInDisplay.DATA_COMPLETION && renderDataCompletionCard()}
						{activeCard === CardInDisplay.TEMPLATE_CHOOSER && renderTemplateChooserCard()}
						{
							activeCard === CardInDisplay.REPORT_GEN_LOADER &&
							<ReportGenProgressCard stateCount={4} />
						}
						{activeCard === CardInDisplay.REGENERATE_REPORT && renderRegenerateReportCard()}
					</div>
				</Modal.Body>

			</Modal>
			{showConfirmation && renderConfirmationModal()}
			{(gdshow || qualshow || quantshow || navAIShow) && renderGeneralDis()}
		</>
	);

}
export default withTranslation()(NewReportModal);
